import { useState, type ReactNode } from "react";

import AccountCheckInCard from "./AccountCheckInCard";
import CheckInPollAccordion from "./CheckInPollAccordion";
import { DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM } from "../../Utils/Constants/sponsorEngagementDefaults";

const SAMPLE_CYCLE_ID = "sample-cycle";
const SAMPLE_PERIOD = "2026-Q3";

const SAMPLE_CYCLE = {
  id: SAMPLE_CYCLE_ID,
  name: "2026-Q3 Check-In",
  period: SAMPLE_PERIOD,
  due_date: "2026-09-30",
  publication_state: "published",
};

const noop = (): void => undefined;

const accountGridClass =
  "grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3.5";

const SAMPLE_ASSIGNED_ACCOUNTS = [
  {
    account_id: "sample-a1",
    account_name: "Northwind Logistics",
    theater: "Americas",
    region: "West",
    sponsor_role: "primary" as const,
    poll1_submitted_at: null as string | null,
  },
  {
    account_id: "sample-a2",
    account_name: "Summit Health Co.",
    theater: "Americas",
    region: "East",
    sponsor_role: "secondary" as const,
    poll1_submitted_at: null,
  },
  {
    account_id: "sample-a3",
    account_name: "Brightline Retail",
    theater: "EMEA",
    region: "EMEA",
    sponsor_role: "primary" as const,
    poll1_submitted_at: null,
  },
];

const SAMPLE_COMPLETED_ACCOUNTS = [
  {
    account_id: "sample-c1",
    account_name: "Northwind Logistics",
    theater: "Americas",
    region: "West",
    sponsor_role: "primary" as const,
    poll1_submitted_at: "2026-07-01T12:00:00Z",
    composite: 4.2,
    tier: "Active",
  },
  {
    account_id: "sample-c2",
    account_name: "Summit Health Co.",
    theater: "Americas",
    region: "East",
    sponsor_role: "secondary" as const,
    poll1_submitted_at: "2026-07-02T12:00:00Z",
    composite: 4.6,
    tier: "Champion",
  },
  {
    account_id: "sample-c3",
    account_name: "Brightline Retail",
    theater: "EMEA",
    region: "EMEA",
    sponsor_role: "primary" as const,
    poll1_submitted_at: "2026-07-03T12:00:00Z",
    composite: 3.8,
    tier: "Active",
  },
];

export const MCI_SAMPLE_MESSAGES = {
  assigned:
    "Preview of assigned check-ins — your program lead will launch the quarterly poll from ExecOps Discovery Hub.",
  completed:
    "Preview of completed check-ins — submitted accounts and scores appear here after you submit each check-in.",
  performance:
    "Preview of My Performance — composite score and account breakdown appear once a poll cycle is active.",
} as const;

export const SAMPLE_MY_PERFORMANCE = {
  period: SAMPLE_PERIOD,
  display_period: SAMPLE_PERIOD,
  is_current_published: true,
  cycle: {
    id: SAMPLE_CYCLE_ID,
    publication_state: "published",
    due_date: "2026-09-30",
  },
  summary: {
    portfolio_composite: 4.35,
    portfolio_tier: "Active",
    rank: 3,
    total_sponsors: 12,
    account_count: 3,
    streak: 2,
    streak_threshold:
      DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM.scoring_config.streak_threshold,
  },
  leaderboard_weights:
    DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM.scoring_config.leaderboard_weights,
  polls: [
    {
      cycle_id: `${SAMPLE_CYCLE_ID}-p1`,
      poll_name: "2026-Q3 · Poll 1",
      index: 1,
      period: SAMPLE_PERIOD,
      published: true,
    },
    {
      cycle_id: `${SAMPLE_CYCLE_ID}-p2`,
      poll_name: "2026-Q2 · Poll 2",
      index: 2,
      period: "2026-Q2",
      published: true,
    },
  ],
  accounts: [
    {
      account_id: "sample-c1",
      account_name: "Northwind Logistics",
      theater: "Americas",
      region: "West",
      sponsor_role: "primary" as const,
      composite: 4.2,
      tier: "Active",
      poll_values: {
        [`${SAMPLE_CYCLE_ID}-p1`]: 4.1,
        [`${SAMPLE_CYCLE_ID}-p2`]: 4.3,
      },
      trend_5q: [3.9, 4.0, 4.1, 4.2, 4.2],
    },
    {
      account_id: "sample-c2",
      account_name: "Summit Health Co.",
      theater: "Americas",
      region: "East",
      sponsor_role: "secondary" as const,
      composite: 4.6,
      tier: "Champion",
      poll_values: {
        [`${SAMPLE_CYCLE_ID}-p1`]: 4.5,
        [`${SAMPLE_CYCLE_ID}-p2`]: 4.7,
      },
      trend_5q: [4.2, 4.3, 4.4, 4.5, 4.6],
    },
    {
      account_id: "sample-c3",
      account_name: "Brightline Retail",
      theater: "EMEA",
      region: "EMEA",
      sponsor_role: "primary" as const,
      composite: 3.8,
      tier: "Active",
      poll_values: {
        [`${SAMPLE_CYCLE_ID}-p1`]: 3.7,
        [`${SAMPLE_CYCLE_ID}-p2`]: 3.9,
      },
      trend_5q: [3.5, 3.6, 3.7, 3.8, 3.8],
    },
  ],
};

const SamplePollAccordion = ({
  poll1Done,
  poll1Total,
  children,
}: {
  poll1Done: number;
  poll1Total: number;
  children: ReactNode;
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <CheckInPollAccordion
      pollName="2026-Q3 Check-In"
      period={SAMPLE_PERIOD}
      cycle={SAMPLE_CYCLE}
      accountCount={poll1Total}
      poll1Done={poll1Done}
      poll1Total={poll1Total}
      isExpanded={expanded}
      onToggleExpand={() => setExpanded(v => !v)}
      showPublicationBadge={false}
    >
      {children}
    </CheckInPollAccordion>
  );
};

export const SampleMyCheckInsAssigned = () => (
  <div className="flex flex-col gap-5 mt-1">
    <SamplePollAccordion poll1Done={0} poll1Total={3}>
      <div className={accountGridClass}>
        {SAMPLE_ASSIGNED_ACCOUNTS.map(row => (
          <AccountCheckInCard
            key={row.account_id}
            account={row}
            isDraft={row.account_id === "sample-a2"}
            showStatusBadge="none"
            onAction={noop}
          />
        ))}
      </div>
    </SamplePollAccordion>
  </div>
);

export const SampleMyCheckInsCompleted = () => (
  <div className="flex flex-col gap-5 mt-1">
    <SamplePollAccordion poll1Done={3} poll1Total={3}>
      <div className={accountGridClass}>
        {SAMPLE_COMPLETED_ACCOUNTS.map(row => (
          <AccountCheckInCard
            key={row.account_id}
            account={row}
            isDraft={false}
            showStatusBadge="none"
            onAction={noop}
          />
        ))}
      </div>
    </SamplePollAccordion>
  </div>
);
